import {MainBadge, MiraIcon, PredictionBadge} from '@mira/ui';
import {Heading, Text} from 'platform/foundation-tw';
import {cn} from 'platform/shared-tw';
import {match} from 'ts-pattern';

import {Fragment, useId, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {assignSafely, getSafely} from 'shared/web';

import {getSectionElementId, SECTION_SCROLL_OFFSET_PX} from '../constants/sectionIds';
import {
  MainCategoryGroup,
  MainCategoryGroupId,
  RowType,
  Subcategory,
  SubcategoryId,
  TransformedCategory,
} from '../schemas';

const SECTION_ANCHOR_STYLE = {scrollMarginTop: `${SECTION_SCROLL_OFFSET_PX}px`} as const;

type SeverityCounts = Record<RowType, number>;
type SeverityCountsMap = Map<MainCategoryGroupId, SeverityCounts>;

const SEVERITY_ORDER = ['damage', 'service', 'suggestions'] as const satisfies readonly RowType[];
const DEFAULT_COUNTS: SeverityCounts = {damage: 0, service: 0, suggestions: 0};

type Translate = ReturnType<typeof useTranslation>['t'];

function getSeverityLabel(type: RowType, t: Translate): string {
  return match(type)
    .with('damage', () => t('predictionReport.severity.damage.label'))
    .with('service', () => t('predictionReport.severity.service.label'))
    .with('suggestions', () => t('predictionReport.severity.suggestions.label'))
    .exhaustive();
}

// Subgrid cascade aligns the forecast column across all subcategory tables.
// Track 3 = `max-content` over every row's forecast cell; track 1 = icon column.
const PAGE_GRID_CLASS =
  'grid [grid-template-columns:var(--width-mira-icon-container)_minmax(0,1fr)_max-content]';

const FULL_ROW_SUBGRID = 'col-span-full grid grid-cols-subgrid';

const TYPE_VARIANT_CLASS: Record<RowType, string> = {
  damage: 'mira-type-damage',
  service: 'mira-type-service',
  suggestions: 'mira-type-suggestions',
};

const ICON_TEXT_CLASS: Record<RowType, string> = {
  damage: 'text-[var(--color-mira-red-70)]',
  service: 'text-[var(--color-mira-orange-70)]',
  suggestions: 'text-[var(--color-mira-purple-70)]',
};

const HEADER_CELL_CLASS =
  'flex min-w-0 items-start text-sm font-normal leading-5 text-[var(--color-text-tertiary)] px-[var(--spacing-mira-md)]';

const FORECAST_TRAILING_PADDING_CLASS = 'pr-8';

const CELL_BASE_CLASS =
  'flex min-w-0 items-start text-sm leading-5 bg-[var(--mira-bg-light)] px-[var(--spacing-mira-md)] py-[var(--spacing-mira-md)]';

const ICON_CELL_CLASS =
  'flex items-center justify-center px-[var(--spacing-mira-md)] py-3 bg-[var(--mira-bg)]';

// Worst-case open height for a subcategory accordion. The page-level subgrid
// (`PAGE_GRID_CLASS`) requires the accordion content to be a direct grid child,
// which blocks the cleaner `grid-template-rows: 0fr/1fr` collapse trick. We
// fall back to a max-height transition; the value is intentionally far above
// any realistic number of inspection rows so content never silently truncates.
const SUBCATEGORY_OPEN_MAX_HEIGHT_PX = 4000;

function getAllSubcategoryIds(mainGroups: MainCategoryGroup[]): SubcategoryId[] {
  return mainGroups.flatMap((mainGroup) =>
    mainGroup.subcategories.map((subcategory) => subcategory.subcategoryId)
  );
}

function computeSeverityCounts(categories: TransformedCategory[]): SeverityCounts {
  let counts: SeverityCounts = {...DEFAULT_COUNTS};

  categories.forEach((category) => {
    counts = assignSafely(counts, category.rowType, getSafely(counts, category.rowType, 0) + 1);
  });

  return counts;
}

function computeAllSeverityCounts(mainGroups: MainCategoryGroup[]): SeverityCountsMap {
  const countsMap: SeverityCountsMap = new Map();

  mainGroups.forEach((mainGroup) => {
    const categories = mainGroup.subcategories.flatMap((subcategory) => subcategory.categories);
    countsMap.set(mainGroup.groupId, computeSeverityCounts(categories));
  });

  return countsMap;
}

interface ListProps {
  mainGroups: MainCategoryGroup[];
}

export function List(props: ListProps) {
  const {t} = useTranslation();
  const severityCountsMap = computeAllSeverityCounts(props.mainGroups);
  const [openSubcategoryIds, setOpenSubcategoryIds] = useState<Set<SubcategoryId>>(new Set());

  if (props.mainGroups.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <Text color="secondary">{t('predictionReport.empty')}</Text>
      </div>
    );
  }

  const allSubcategoryIds = getAllSubcategoryIds(props.mainGroups);
  const areAllOpen = allSubcategoryIds.every((id) => openSubcategoryIds.has(id));

  const handleToggleSubcategory = (subcategoryId: SubcategoryId) => {
    setOpenSubcategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(subcategoryId)) {
        next.delete(subcategoryId);
        return next;
      }
      next.add(subcategoryId);
      return next;
    });
  };

  const handleToggleAll = () => {
    setOpenSubcategoryIds(areAllOpen ? new Set() : new Set(allSubcategoryIds));
  };

  return (
    <div className={PAGE_GRID_CLASS}>
      <div className="col-span-full mb-6 flex">
        <ToggleAllButton areAllOpen={areAllOpen} onToggle={handleToggleAll} />
      </div>
      {props.mainGroups.map((mainGroup, mgIndex) => {
        const isLastGroup = mgIndex === props.mainGroups.length - 1;
        const severityCounts = severityCountsMap.get(mainGroup.groupId) ?? DEFAULT_COUNTS;

        return (
          <Fragment key={mainGroup.groupId}>
            <MainGroupHeader
              mainGroup={mainGroup}
              severityCounts={severityCounts}
              isFirst={mgIndex === 0}
            />
            {mainGroup.subcategories.map((subcategory, scIndex) => (
              <SubcategorySection
                key={subcategory.subcategoryId}
                subcategory={subcategory}
                isFirstInGroup={scIndex === 0}
                isOpen={openSubcategoryIds.has(subcategory.subcategoryId)}
                onToggle={handleToggleSubcategory}
              />
            ))}
            {!isLastGroup && (
              <div className="col-span-full mt-8 h-px w-full bg-[var(--color-mira-gray-10)]" />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

interface ToggleAllButtonProps {
  areAllOpen: boolean;
  onToggle: () => void;
}

function ToggleAllButton(props: ToggleAllButtonProps) {
  const {t} = useTranslation();

  return (
    <button
      type="button"
      onClick={props.onToggle}
      aria-expanded={props.areAllOpen}
      className="group -ml-2.5 flex cursor-pointer items-center gap-1.5 rounded-sm bg-transparent px-2.5 py-1.5 text-sm font-medium text-[var(--color-text-secondary)] transition-colors duration-200 ease-in-out hover:bg-[var(--color-mira-gray-5)] hover:text-[var(--color-text-primary)] focus-visible:bg-[var(--color-mira-gray-5)] focus-visible:text-[var(--color-text-primary)] focus-visible:outline-none"
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-6 items-center justify-center text-2xl leading-none font-light transition-transform duration-300 ease-in-out group-hover:scale-110',
          props.areAllOpen && 'rotate-45'
        )}
      >
        +
      </span>
      {props.areAllOpen ? t('predictionReport.collapseAll') : t('predictionReport.expandAll')}
    </button>
  );
}

interface MainGroupHeaderProps {
  mainGroup: MainCategoryGroup;
  severityCounts: SeverityCounts;
  isFirst: boolean;
}

function MainGroupHeader(props: MainGroupHeaderProps) {
  return (
    <div className={cn('col-span-full pb-6', !props.isFirst && 'mt-8')}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <MiraIcon name={props.mainGroup.icon} />
          <div id={getSectionElementId(props.mainGroup.groupId)} style={SECTION_ANCHOR_STYLE}>
            <Heading size={2}>{props.mainGroup.title}</Heading>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {SEVERITY_ORDER.map((type) => {
            const count = getSafely(props.severityCounts, type, 0);
            if (count === 0) {
              return null;
            }
            return <MainBadge key={type} type={type} count={count} />;
          })}
        </div>
      </div>
      {props.mainGroup.description && (
        <div className="max-w-[40rem] pt-5 pl-10">
          <Text color="secondary" size="small">
            {props.mainGroup.description}
          </Text>
        </div>
      )}
    </div>
  );
}

interface SubcategorySectionProps {
  subcategory: Subcategory;
  isFirstInGroup: boolean;
  isOpen: boolean;
  onToggle: (subcategoryId: SubcategoryId) => void;
}

function SubcategorySection(props: SubcategorySectionProps) {
  const titleId = useId();
  const panelId = useId();
  const severityCounts = computeSeverityCounts(props.subcategory.categories);

  const handleToggle = () => props.onToggle(props.subcategory.subcategoryId);

  return (
    <Fragment>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={props.isOpen}
        aria-controls={panelId}
        className={cn(
          FULL_ROW_SUBGRID,
          'box-border w-full cursor-pointer items-center rounded-sm bg-[var(--color-mira-gray-5)]',
          !props.isFirstInGroup && 'mt-4'
        )}
      >
        <div className="flex h-[var(--width-mira-icon-container)] items-center justify-center">
          <span
            aria-hidden="true"
            className="flex size-7 items-center justify-center text-2xl leading-none font-light text-[var(--color-text-primary)] transition-transform duration-200 ease-in-out hover:scale-110"
          >
            {props.isOpen ? '×' : '+'}
          </span>
        </div>
        <div className="col-span-2 flex min-w-0 items-center justify-between gap-3 p-3">
          <div className="flex min-w-0 items-baseline gap-3">
            <Text id={titleId} size="large" color="primary" alternative noWrap>
              {props.subcategory.title}
            </Text>
            <div className="flex items-center gap-1">
              <Text size="large" color="tertiary" noWrap>
                (
              </Text>
              <Text size="large" color="primary" noWrap>
                {props.subcategory.count}
              </Text>
              <Text size="large" color="tertiary" noWrap>
                )
              </Text>
            </div>
          </div>
          <SubcategorySeverityBadges counts={severityCounts} />
        </div>
      </button>

      <div
        id={panelId}
        role="table"
        aria-labelledby={titleId}
        aria-hidden={!props.isOpen}
        className={cn(
          FULL_ROW_SUBGRID,
          'gap-y-[var(--spacing-1)] overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out'
        )}
        style={{
          maxHeight: props.isOpen ? `${SUBCATEGORY_OPEN_MAX_HEIGHT_PX}px` : '0',
          opacity: props.isOpen ? 1 : 0,
        }}
      >
        <HeaderRow />
        {props.subcategory.categories.map((category) => (
          <DataRow key={category.categoryKey} category={category} />
        ))}
      </div>
    </Fragment>
  );
}

interface SubcategorySeverityBadgesProps {
  counts: SeverityCounts;
}

function SubcategorySeverityBadges(props: SubcategorySeverityBadgesProps) {
  const {t} = useTranslation();

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {SEVERITY_ORDER.map((type) => {
        const count = getSafely(props.counts, type, 0);
        if (count === 0) {
          return null;
        }
        return (
          <MainBadge
            key={type}
            type={type}
            count={count}
            size="sm"
            label={getSeverityLabel(type, t)}
          />
        );
      })}
    </div>
  );
}

function HeaderRow() {
  const {t} = useTranslation();
  return (
    <div
      role="row"
      className={cn(FULL_ROW_SUBGRID, 'pt-[var(--spacing-mira-md)] pb-[var(--spacing-mira-md)]')}
    >
      <span role="columnheader" aria-hidden="true" />
      <span role="columnheader" className={HEADER_CELL_CLASS}>
        {t('predictionReport.table.operation')}
      </span>
      <span role="columnheader" className={cn(HEADER_CELL_CLASS, FORECAST_TRAILING_PADDING_CLASS)}>
        {t('predictionReport.table.forecast')}
      </span>
    </div>
  );
}

interface DataRowProps {
  category: TransformedCategory;
}

function DataRow(props: DataRowProps) {
  return (
    <div
      role="row"
      className={cn(
        FULL_ROW_SUBGRID,
        'overflow-hidden rounded-sm',
        TYPE_VARIANT_CLASS[props.category.rowType]
      )}
    >
      <div role="cell" className={cn(ICON_CELL_CLASS, ICON_TEXT_CLASS[props.category.rowType])}>
        <MiraIcon name={props.category.rowIcon} />
      </div>
      <div
        role="cell"
        className={cn(CELL_BASE_CLASS, 'font-medium text-[var(--color-mira-blue-100)]')}
      >
        {props.category.operationDescription}
      </div>
      <div
        role="cell"
        className={cn(
          CELL_BASE_CLASS,
          'font-normal text-[var(--color-text-primary)]',
          FORECAST_TRAILING_PADDING_CLASS
        )}
      >
        <PredictionBadge
          percentage={props.category.percentage}
          label={props.category.label}
          willNeed={props.category.will_need}
        />
      </div>
    </div>
  );
}
